import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { MaintenanceSummaryDto } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import {
  ConfigurationClassDto,
  MaintenanceDueDto,
  MaintenanceExportLogEntryDto,
  MaintenanceExportStatus,
  MaintenanceParameterValueDto,
  Snapshot,
  SnapshotMaintenanceParametersDto,
} from 'src/app/shared/flight-log-api/maintenance/maintenance-client.model';
import { coerceHttpParams } from 'src/app/shared/flight-log-api/shared/create-request-options';
import { assertNotNull, assertNotNullOrWhitespace } from 'src/util/values';
import { isNullOrEmpty } from '../../../../util/string';
import { FLIGHT_LOG_API_CONFIG, FlightLogApiConfiguration } from '../flight-log-api-configuration';

export interface MaintenanceLogExportQueryParams {
  flightLogId?: string;
  aircraftId?: string;
  baseId?: string;
  companyId?: string;
  status?: MaintenanceExportStatus;
  startTime?: Date;
  endTime?: Date;
}

export interface MaintenanceDueQueryParams {
  aircraftId?: string;
  companyId?: string;
  startTime?: Date;
  endTime?: Date;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceHttpClient {
  constructor(
    @Inject(FLIGHT_LOG_API_CONFIG) private _config: FlightLogApiConfiguration,
    private _http: HttpClient,
  ) {}

  public getExportLogEntryById(
    maintenanceExportLogEntryId: string,
  ): Observable<MaintenanceExportLogEntryDto> {
    assertNotNullOrWhitespace(maintenanceExportLogEntryId, 'maintenanceExportLogEntryId');
    return this._http.get<MaintenanceExportLogEntryDto>(
      this._url(`maintenanceExportLogs/${maintenanceExportLogEntryId}`),
    );
  }

  public queryExportLogs({
    flightLogId,
    aircraftId,
    baseId,
    companyId,
    status,
    startTime,
    endTime,
  }: MaintenanceLogExportQueryParams): Observable<MaintenanceExportLogEntryDto[]> {
    const params: any = {
      limit: 10,
    };

    if (flightLogId != null) {
      params.flightLogId = flightLogId;
    }
    if (aircraftId != null) {
      params.aircraftId = aircraftId;
    }
    if (baseId != null) {
      params.baseId = baseId;
    }
    if (companyId != null) {
      params.companyId = companyId;
    }
    if (status != null) {
      params.status = status;
    }

    const st = moment(startTime);
    const et = moment(endTime);

    if (st.isValid()) {
      params.startTime = st.startOf('day').utc().format();
    }
    if (et.isValid()) {
      params.endTime = et.endOf('day').utc().format();
    }
    return this._http.get<MaintenanceExportLogEntryDto[]>(this._url(`maintenanceExportLogs?`), {
      params: coerceHttpParams(params),
    });
  }

  public getMaintenanceDueById(id: string): Observable<MaintenanceDueDto> {
    assertNotNullOrWhitespace(id, 'maintenanceDueId');
    return this._http.get<MaintenanceDueDto>(this._url(`maintenanceDue/${id}`));
  }

  public getConfigurationClassById(id: string): Observable<ConfigurationClassDto> {
    assertNotNullOrWhitespace(id, 'configurationClassId');
    return this._http.get<ConfigurationClassDto>(this._url(`configurationClasses/${id}`));
  }

  public queryMaintenanceDue({
    aircraftId,
    companyId,
    startTime,
    endTime,
  }: MaintenanceDueQueryParams): Observable<MaintenanceDueDto[]> {
    const params: any = {
      limit: 10,
    };

    if (aircraftId != null) {
      params.aircraftId = aircraftId;
    }
    if (companyId != null) {
      params.companyId = companyId;
    }

    const st = moment(startTime);
    const et = moment(endTime);

    if (st.isValid()) {
      params.startTime = st.startOf('day').utc().format();
    }
    if (et.isValid()) {
      params.endTime = et.endOf('day').utc().format();
    }
    return this._http.get<MaintenanceDueDto[]>(this._url(`maintenanceDue`), {
      params: coerceHttpParams(params),
    });
  }

  public getSnapshots(aircraftId: string): Observable<Snapshot[]> {
    assertNotNullOrWhitespace(aircraftId, 'aircraftId');
    return this._http.get<Snapshot[]>(
      this._url(`maintenanceParameters/snapshots?aircraftId=${aircraftId}`),
    );
  }

  public getRamcoMaintenanceParameterTotals(
    aircraftId: string,
  ): Observable<MaintenanceParameterValueDto[]> {
    assertNotNullOrWhitespace(aircraftId, 'aircraftId');
    return this._http.get<MaintenanceParameterValueDto[]>(
      this._url(`maintenanceParameters/ramco-totals`),
      {
        params: { aircraftId },
      },
    );
  }

  public getFlightLogMaintenanceParameterTotals(
    aircraftId: string,
    untilFlightLogId?: string,
    untilFlightTime?: Date,
  ): Observable<MaintenanceSummaryDto> {
    assertNotNullOrWhitespace(aircraftId, 'aircraftId');
    const params: any = { aircraftId };
    if (!isNullOrEmpty(untilFlightLogId)) {
      params.untilFlightLogId = untilFlightLogId;
    }
    if (untilFlightTime != null) {
      params.untilFlightTime = moment(untilFlightTime).format();
    }
    const url = this._url(`maintenanceParameters/log-totals?`);
    return this._http.get<MaintenanceSummaryDto>(url, {
      params: coerceHttpParams(params),
    });
  }

  public snapshotMaintenanceParameters(snapshot: SnapshotMaintenanceParametersDto) {
    const url = this._url('maintenanceParameters/snapshot');
    return this._http.put(url, snapshot);
  }

  public getFlightLogMaintenanceImageById(flightLogId: string): Observable<any> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    return this._http.get(this._url(`files/${flightLogId}/maintenance/image`), {
      observe: 'response',
      responseType: 'blob',
    });
  }

  public getFlightLogMaintenanceImageNameById(flightLogId: string): Observable<any> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    return this._http.get(this._url(`files/${flightLogId}/maintenance/image/name`), {
      responseType: 'json',
    });
  }

  public getFlightLogMaintenanceImageNamesByIds(flightLogIds: string[]): Observable<any> {
    assertNotNull(flightLogIds, 'flightLogIds');
    return this._http.post(this._url(`files/maintenance/image/names`), flightLogIds, {
      responseType: 'json',
    });
  }

  public removeFlightLogMaintenanceImageById(flightLogId: string): Observable<any> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    return this._http.delete(this._url(`files/${flightLogId}/maintenance/image`), {
      responseType: 'json',
    });
  }

  public uploadFlightLogMaintenanceImageById(
    flightLogId: string,
    formData: FormData,
  ): Observable<any> {
    assertNotNullOrWhitespace(flightLogId, 'flightLogId');
    return this._http.put<any>(this._url(`files/${flightLogId}/maintenance/image`), formData, {
      reportProgress: true,
      observe: 'events',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { 'Content-Type': 'undefined' },
    });
  }

  private _url(path: string) {
    return this._config.baseUri + '/api/' + path;
  }
}
